import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { apiUrl } from "../apiUrl";

// Helper para sessionStorage
const authStorage = {
  get: () => {
    try {
      const data = sessionStorage.getItem("auth");
      return data ? JSON.parse(data) : null;
    } catch (error) {
      console.error("Error reading auth from sessionStorage", error);
      return null;
    }
  },
  set: (authData) => {
    try {
      sessionStorage.setItem("auth", JSON.stringify(authData));
    } catch (error) {
      console.error("Error saving auth to sessionStorage", error);
    }
  },
  clear: () => {
    try {
      sessionStorage.removeItem("auth");
    } catch (error) {
      console.error("Error clearing auth from sessionStorage", error);
    }
  },
};

export const loginUser = createAsyncThunk(
  "auth/login",
  async (credentials, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(
        `${apiUrl}/api/v1/auth/login`,
        credentials
      );
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const logoutUser = createAsyncThunk(
  "auth/logout",
  async (_, { getState }) => {
    const { token } = getState().auth;
    try {
      await axios.post(
        `${apiUrl}/api/v1/auth/logout`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
    } catch (error) {
      console.error("Logout error:", error);
    }
  }
);

export const checkAuth = createAsyncThunk(
  "auth/check",
  async (_, { getState }) => {
    const { token } = getState().auth;
    if (!token) throw new Error("No token available");

    try {
      const { data } = await axios.get(`${apiUrl}/api/v1/auth/check`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }
);

export const changePassword = createAsyncThunk(
  "auth/changePassword",
  async (passwords, { getState, rejectWithValue }) => {
    const { token } = getState().auth;
    try {
      const { data } = await axios.post(
        `${apiUrl}/api/v1/auth/cambiar-contra`,
        passwords,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const initialState = {
  user: null,
  token: null,
  status: "idle",
  checkStatus: "idle",
  changePasswordStatus: "idle",
  error: null,
  ...(authStorage.get() || {}), // Carga inicial desde sessionStorage
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    clearAuthError: (state) => {
      state.error = null;
    },
    resetChangePasswordStatus: (state) => {
      state.changePasswordStatus = "idle";
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(loginUser.pending, (state) => {
        state.status = "loading";
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.error = null;
        authStorage.set({
          user: action.payload.user,
          token: action.payload.token,
        });
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
        authStorage.clear();
      })

      // Logout
      .addCase(logoutUser.fulfilled, (state) => {
        state.user = null;
        state.token = null;
        state.status = "idle";
        authStorage.clear();
      })

      // Check auth
      .addCase(checkAuth.pending, (state) => {
        state.checkStatus = "loading";
      })
      .addCase(checkAuth.fulfilled, (state, action) => {
        state.checkStatus = "succeeded";
        state.user = action.payload.user;
        authStorage.set({ user: action.payload.user, token: state.token });
      })
      .addCase(checkAuth.rejected, (state) => {
        state.checkStatus = "failed";
        state.user = null;
        state.token = null;
        authStorage.clear();
      })

      // Change password
      .addCase(changePassword.pending, (state) => {
        state.changePasswordStatus = "loading";
        state.error = null;
      })
      .addCase(changePassword.fulfilled, (state) => {
        state.changePasswordStatus = "succeeded";
        state.error = null;
        state.token = null;
        state.user = null;
        authStorage.clear();
      })
      .addCase(changePassword.rejected, (state, action) => {
        state.changePasswordStatus = "failed";
        state.error = action.payload;
      });
  },
});

export const { clearAuthError } = authSlice.actions;

// Selectores
export const selectCurrentUser = (state) => state.auth.user;
export const selectAuthToken = (state) => state.auth.token;
export const selectIsAuthenticated = (state) => !!state.auth.token;
export const selectAuthStatus = (state) => state.auth.status;
export const selectCheckAuthStatus = (state) => state.auth.checkStatus;
export const selectChangePasswordStatus = (state) =>
  state.auth.changePasswordStatus;
export const selectAuthError = (state) => state.auth.error;

export default authSlice.reducer;
